/**
 * Chat panel overlay — programmatically created DOM.
 * Glass-morphism aesthetic matching existing HUD.
 * Per-worker message history, thinking indicator, send on Enter.
 */

import { apiClient } from '@/lib/api-client';
import type { ChatMessage, TaskDeliverable } from '@/lib/api-client';
import type { ResearchHeadline, StockAnalysisData } from '@/types';
import type { TaskPlan } from '@/data/types';
import { TYPEWRITER_CHAR_DELAY } from '@/utils/constants';
import { dataManager } from '@/data/data-manager';
import { formatMarkdown, escapeHtml } from '@/utils/markdown';

// Fallback names when DataManager hasn't loaded yet
const WORKER_NAMES = ['Alex', 'Sam', 'Jordan', 'Casey'];
const WORKER_ROLES = ['Manager', 'Designer', 'Product Manager', 'Marketing'];

export class ChatPanel {
  private container: HTMLElement;
  private messagesEl: HTMLElement;
  private inputEl: HTMLTextAreaElement;
  private sendBtn: HTMLElement;
  private headerNameEl: HTMLElement;
  private headerRoleEl: HTMLElement;
  private toggleBtn: HTMLElement;
  private statusDot: HTMLElement;

  private isOpen = false;
  private activeWorkerIndex = 0;
  private messageHistory: Map<string, ChatMessage[]> = new Map();
  private isWaiting = false;

  // Onboarding mode
  private onboardingMode = false;
  private headerOverrideName: string | null = null;
  private headerOverrideRole: string | null = null;
  private activeTypewriterInterval: ReturnType<typeof setInterval> | null = null;
  private greetedWorkers: Set<number> = new Set();
  private _interceptPlan = false;
  private _lastPlanPresentedAt = 0;

  constructor() {
    // Create toggle button
    this.toggleBtn = document.createElement('button');
    this.toggleBtn.id = 'chat-toggle';
    this.toggleBtn.innerHTML = '💬';
    this.toggleBtn.title = 'Open Chat';
    document.body.appendChild(this.toggleBtn);

    // Create panel container
    this.container = document.createElement('div');
    this.container.id = 'chat-panel';
    this.container.innerHTML = `
      <div class="chat-header">
        <div class="chat-header-info">
          <span class="chat-status-dot"></span>
          <span class="chat-header-name">Alex</span>
          <span class="chat-header-role">Engineer</span>
        </div>
        <button class="chat-close-btn">×</button>
      </div>
      <div class="chat-messages"></div>
      <div class="chat-input-row">
        <textarea class="chat-input" rows="1" placeholder="Message Alex..."></textarea>
        <button class="chat-send-btn">→</button>
      </div>
    `;
    document.body.appendChild(this.container);

    // Cache elements
    this.messagesEl = this.container.querySelector('.chat-messages')!;
    this.inputEl = this.container.querySelector('.chat-input') as HTMLTextAreaElement;
    this.sendBtn = this.container.querySelector('.chat-send-btn')!;
    this.headerNameEl = this.container.querySelector('.chat-header-name')!;
    this.headerRoleEl = this.container.querySelector('.chat-header-role')!;
    this.statusDot = this.container.querySelector('.chat-status-dot')!;

    const closeBtn = this.container.querySelector('.chat-close-btn')!;

    // Event listeners
    this.toggleBtn.addEventListener('click', () => this.toggle());
    closeBtn.addEventListener('click', () => this.close());
    this.sendBtn.addEventListener('click', () => this.send());
    this.inputEl.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        this.send();
      }
      // Shift+Enter = new line (default textarea behavior, no need to handle)
    });
    // Auto-resize textarea as user types
    this.inputEl.addEventListener('input', () => {
      this.inputEl.style.height = 'auto';
      this.inputEl.style.height = Math.min(this.inputEl.scrollHeight, 120) + 'px';
    });

    // Listen for worker selection changes from HUD
    window.addEventListener('worker-selected', ((e: CustomEvent) => {
      this.setActiveWorker(e.detail.workerIndex);
    }) as EventListener);

    // Listen for SSE events
    this.setupSSEListeners();

    // Connection status indicator
    apiClient.onConnectionChange((connected) => {
      this.setConnected(connected);
    });
  }

  private setupSSEListeners(): void {
    // Agent state changes — show thinking indicator
    apiClient.onSSE('agent-state', (event) => {
      const { state, workerId } = event.data as { state: string; workerId: string };
      const activeWorkerId = `worker-${this.activeWorkerIndex}`;

      if (workerId === activeWorkerId) {
        if (state === 'thinking') {
          this.showThinking();
        }
      }
    });

    // Chat responses via SSE
    apiClient.onSSE('chat-response', (event) => {
      const { workerId, message } = event.data as { workerId: string; message: string };
      const activeWorkerId = `worker-${this.activeWorkerIndex}`;

      // Store in history regardless of which worker is active
      this.addToHistory(workerId, {
        role: 'assistant',
        content: message,
        workerId,
        timestamp: Date.now(),
      });

      // If it's the active worker, render it
      if (workerId === activeWorkerId) {
        this.hideThinking();
        this.renderMessages();
      }
    });
  }

  private getWorkerId(): string {
    return `worker-${this.activeWorkerIndex}`;
  }

  private getWorkerName(): string {
    if (dataManager.initialized) {
      return dataManager.getWorkerName(this.activeWorkerIndex);
    }
    return WORKER_NAMES[this.activeWorkerIndex] || `Worker ${this.activeWorkerIndex + 1}`;
  }

  private getWorkerRole(): string {
    if (dataManager.initialized) {
      return dataManager.getWorkerRole(this.activeWorkerIndex);
    }
    return WORKER_ROLES[this.activeWorkerIndex] || 'Worker';
  }

  toggle(): void {
    if (this.isOpen) this.close();
    else this.open();
  }

  open(): void {
    this.isOpen = true;
    this.container.classList.add('open');
    this.toggleBtn.classList.add('hidden');
    this.inputEl.focus();
    this.renderMessages();
    this.scrollToBottom();
  }

  close(): void {
    this.isOpen = false;
    this.container.classList.remove('open');
    this.toggleBtn.classList.remove('hidden');
  }

  show(): void {
    this.toggleBtn.style.display = 'flex';
    // Connect SSE when entering interior
    apiClient.connectSSE();
    // Pre-wake agent (handles cold starts gracefully)
    apiClient.wakeAgent();
  }

  hide(): void {
    this.close();
    this.toggleBtn.style.display = 'none';
    apiClient.disconnectSSE();
  }

  /**
   * Open the chat panel for a specific worker by clicking on them.
   * Adds a greeting message the first time a worker is selected.
   */
  openForWorker(index: number): void {
    if (this.onboardingMode) return;

    this.setActiveWorker(index);

    // Dispatch worker-selected for HUD tab sync
    window.dispatchEvent(new CustomEvent('worker-selected', {
      detail: { workerIndex: index },
    }));

    // Load persistent conversation from backend (if available)
    const workerId = `worker-${index}`;
    if (!this.messageHistory.has(workerId) && dataManager.initialized) {
      dataManager.getConversation(index).then((messages) => {
        if (messages.length > 0) {
          // Convert backend messages to ChatMessage format
          for (const msg of messages) {
            this.addToHistory(workerId, {
              role: msg.role,
              content: msg.content,
              workerId,
              timestamp: msg.timestamp,
            });
          }
          this.greetedWorkers.add(index);
          if (this.isOpen && this.getWorkerId() === workerId) {
            this.renderMessages();
            this.scrollToBottom();
          }
        }
      }).catch((err) => {
        console.warn('Failed to load conversation:', err);
      });
    }

    // Add greeting if this is the first interaction (and no persisted history)
    if (!this.greetedWorkers.has(index) && !this.messageHistory.has(workerId)) {
      this.greetedWorkers.add(index);
      this.addToHistory(workerId, {
        role: 'assistant',
        content: `Hey boss, what's on your mind?`,
        workerId,
        timestamp: Date.now(),
      });
    }

    this.open();
    this.renderMessages();
    this.scrollToBottom();
  }

  setActiveWorker(index: number): void {
    // Ignore worker switching during onboarding
    if (this.onboardingMode) return;

    this.activeWorkerIndex = index;
    this.headerNameEl.textContent = this.getWorkerName();
    this.headerRoleEl.textContent = this.getWorkerRole();
    this.inputEl.placeholder = `Message ${this.getWorkerName()}...`;
    this.hideThinking();
    this.renderMessages();
    this.scrollToBottom();
  }

  private async send(): Promise<void> {
    const text = this.inputEl.value.trim();
    if (!text || this.isWaiting || this.onboardingMode) return;

    const workerId = this.getWorkerId();

    // Add user message to history
    const userMsg: ChatMessage = {
      role: 'user',
      content: text,
      workerId,
      timestamp: Date.now(),
    };
    this.addToHistory(workerId, userMsg);

    // Clear input, reset height, and render
    this.inputEl.value = '';
    this.inputEl.style.height = 'auto';
    this.renderMessages();
    this.scrollToBottom();
    this.showThinking();

    // Auto-intercept plans for Alex (worker-0) BEFORE dispatching events
    // This prevents a race condition where the plan arrives before the flag is set
    if (this.activeWorkerIndex === 0) {
      this._interceptPlan = true;
    }

    // Dispatch event so worker enters THINKING state
    window.dispatchEvent(new CustomEvent('worker-thinking', {
      detail: { workerId, thinking: true },
    }));

    // Dispatch masking event — worker walks to whiteboard while AI processes
    window.dispatchEvent(new CustomEvent('chat-message-sent', {
      detail: { workerId, workerIndex: this.activeWorkerIndex },
    }));

    let interceptedPlan: TaskPlan | undefined;

    try {
      const response = await apiClient.sendChat(text, workerId);

      // Check if we should intercept the task plan for choreographed presentation
      if (this._interceptPlan && response.taskPlan) {
        // Store the plan — DON'T add it to message history or render it
        // The main.ts choreography will present it via addAlexPlanPresentation()
        interceptedPlan = response.taskPlan;
      } else {
        // Normal flow: add assistant message (with deliverable and/or task plan)
        const assistantMsg: ChatMessage = {
          role: 'assistant',
          content: response.message,
          workerId,
          timestamp: Date.now(),
          deliverable: response.deliverable,
          taskPlan: response.taskPlan,
        };
        this.addToHistory(workerId, assistantMsg);
      }

      // If action was returned, trigger it
      if (response.workerAction) {
        window.dispatchEvent(new CustomEvent('action-requested', {
          detail: { actionId: response.workerAction, workerIndex: this.activeWorkerIndex },
        }));
      }
    } catch (err) {
      // Add error message
      const errMsg: ChatMessage = {
        role: 'assistant',
        content: 'Sorry, I had trouble responding. Please try again.',
        workerId,
        timestamp: Date.now(),
      };
      this.addToHistory(workerId, errMsg);
    } finally {
      this.hideThinking();
      this.isWaiting = false;

      // Worker done thinking — walk back from whiteboard
      window.dispatchEvent(new CustomEvent('worker-thinking', {
        detail: { workerId, thinking: false },
      }));
      window.dispatchEvent(new CustomEvent('chat-response-received', {
        detail: {
          workerId,
          workerIndex: this.activeWorkerIndex,
          hasTaskPlan: !!interceptedPlan,
          taskPlan: interceptedPlan || null,
        },
      }));

      // Only render messages if we didn't intercept a plan
      // (if intercepted, main.ts will call addAlexPlanPresentation later)
      if (!interceptedPlan && this.getWorkerId() === workerId) {
        this.renderMessages();
        this.scrollToBottom();
      }
    }
  }

  private addToHistory(workerId: string, msg: ChatMessage): void {
    if (!this.messageHistory.has(workerId)) {
      this.messageHistory.set(workerId, []);
    }
    this.messageHistory.get(workerId)!.push(msg);
  }

  private showThinking(): void {
    this.isWaiting = true;
    // Remove existing thinking indicator if any
    this.hideThinking();

    const thinkingEl = document.createElement('div');
    thinkingEl.className = 'chat-msg assistant thinking';
    thinkingEl.innerHTML = `
      <span class="chat-msg-name">${this.getWorkerName()}</span>
      <div class="thinking-bubble">
        <span class="thinking-dots"><span></span><span></span><span></span></span>
        <span class="thinking-label">Thinking...</span>
      </div>
    `;
    this.messagesEl.appendChild(thinkingEl);
    this.scrollToBottom();
  }

  private hideThinking(): void {
    const existing = this.messagesEl.querySelector('.thinking');
    if (existing) existing.remove();
  }

  private renderMessages(): void {
    // Clear all except thinking indicator
    this.hideThinking();
    this.messagesEl.innerHTML = '';

    const workerId = this.getWorkerId();
    const messages = this.messageHistory.get(workerId) || [];

    if (messages.length === 0 && !this.onboardingMode) {
      const emptyEl = document.createElement('div');
      emptyEl.className = 'chat-empty';
      emptyEl.textContent = `Start a conversation with ${this.getWorkerName()}`;
      this.messagesEl.appendChild(emptyEl);
      return;
    }

    for (const msg of messages) {
      const el = document.createElement('div');
      el.className = `chat-msg ${msg.role}`;

      if (msg.role === 'assistant') {
        let html = `
          <span class="chat-msg-name">${this.getWorkerName()}</span>
          <span class="chat-msg-text">${formatMarkdown(msg.content)}</span>
        `;
        // Render deliverable card if present
        if (msg.deliverable) {
          html += this.renderDeliverable(msg.deliverable);
        }
        el.innerHTML = html;

        // Render task plan card if present (appended after the message element)
        if (msg.taskPlan) {
          this.messagesEl.appendChild(el);
          this.renderTaskPlanCard(msg.taskPlan);
          continue; // Skip the appendChild below since we already appended
        }

        // Wire up toggle for deliverable collapse
        if (msg.deliverable) {
          const toggleBtn = el.querySelector('.deliverable-toggle');
          const body = el.querySelector('.deliverable-body');
          if (toggleBtn && body) {
            toggleBtn.addEventListener('click', () => {
              body.classList.toggle('collapsed');
              toggleBtn.textContent = body.classList.contains('collapsed') ? 'Show' : 'Hide';
            });
          }
        }
      } else {
        el.innerHTML = `
          <span class="chat-msg-name">You</span>
          <span class="chat-msg-text">${escapeHtml(msg.content)}</span>
        `;
      }

      this.messagesEl.appendChild(el);
    }
  }

  private renderDeliverable(deliverable: TaskDeliverable): string {
    return `
      <div class="deliverable-card">
        <div class="deliverable-header">
          <span class="deliverable-icon">📋</span>
          <span class="deliverable-title">${escapeHtml(deliverable.title)}</span>
          <button class="deliverable-toggle">Hide</button>
        </div>
        <div class="deliverable-body">
          <pre class="deliverable-content">${escapeHtml(deliverable.content)}</pre>
        </div>
      </div>
    `;
  }

  private scrollToBottom(): void {
    requestAnimationFrame(() => {
      this.messagesEl.scrollTop = this.messagesEl.scrollHeight;
    });
  }

  setConnected(connected: boolean): void {
    this.statusDot.className = `chat-status-dot ${connected ? 'connected' : 'disconnected'}`;
  }

  // ── Onboarding Methods ──────────────────────────────────────

  /**
   * Enable/disable onboarding mode.
   * When active: user input disabled, worker switching locked.
   */
  setOnboardingMode(active: boolean): void {
    this.onboardingMode = active;
    this.inputEl.disabled = active;
    this.sendBtn.style.opacity = active ? '0.3' : '1';
    this.sendBtn.style.pointerEvents = active ? 'none' : 'auto';
    if (active) {
      this.inputEl.placeholder = 'Watch the demo...';
      // Clear any empty-state or leftover content so onboarding messages start at top
      this.messagesEl.innerHTML = '';
    } else {
      this.inputEl.placeholder = `Message ${this.headerOverrideName || this.getWorkerName()}...`;
    }
  }

  /**
   * Override the chat header to show a custom name/role (e.g. "CEO").
   */
  setHeaderOverride(name: string, role: string): void {
    this.headerOverrideName = name;
    this.headerOverrideRole = role;
    this.headerNameEl.textContent = name;
    this.headerRoleEl.textContent = role;
  }

  clearHeaderOverride(): void {
    this.headerOverrideName = null;
    this.headerOverrideRole = null;
    this.headerNameEl.textContent = this.getWorkerName();
    this.headerRoleEl.textContent = this.getWorkerRole();
    this.inputEl.placeholder = `Message ${this.getWorkerName()}...`;
  }

  // ── Plan Interception (choreographed Alex flow) ─────────

  /**
   * When enabled, `send()` will NOT render task plans inline.
   * Instead, it passes the plan through the `chat-response-received` event
   * so main.ts can choreograph Alex's presentation.
   */
  setInterceptPlan(enabled: boolean): void {
    this._interceptPlan = enabled;
  }

  /**
   * Choreographed plan presentation: adds Alex dialogue + plan card.
   * Called from main.ts after Alex walks to front-center.
   */
  addAlexPlanPresentation(plan: TaskPlan): void {
    // Dedup guard — skip if called within 2s of last call (prevents double presentation)
    const now = Date.now();
    if (now - this._lastPlanPresentedAt < 2000) {
      console.warn('[ChatPanel] Duplicate addAlexPlanPresentation call — skipping');
      return;
    }
    this._lastPlanPresentedAt = now;

    const workerId = this.getWorkerId();

    // Step 1: Add initial dialogue — Alex describes the plan in plain language
    const taskSummary = plan.tasks
      ? plan.tasks.map(t => t.description.split(' ').slice(0, 6).join(' ')).join(', ')
      : 'the work';
    const workerCount = plan.tasks ? new Set(plan.tasks.map(t => t.workerRole)).size : 1;
    const teamNote = workerCount > 1
      ? `I can get ${workerCount} people on this.`
      : "I'll handle this personally.";

    const dialogueMsg: ChatMessage = {
      role: 'assistant',
      content: `Love it, boss! ${teamNote} Should I show you the full game plan, or do you want to chat about it first?`,
      workerId,
      timestamp: Date.now(),
    };
    this.addToHistory(workerId, dialogueMsg);
    this.renderMessages();
    this.scrollToBottom();

    // Step 2: "Sounds good, let's go!" button — user controls the pace
    this.addInlineButton("Sounds good, let's go!", () => {
      // Step 3: Alex responds, then shows the plan card
      const followUp: ChatMessage = {
        role: 'assistant',
        content: "Great, here's my plan for your approval!",
        workerId,
        timestamp: Date.now(),
      };
      this.addToHistory(workerId, followUp);
      this.renderMessages();
      this.scrollToBottom();

      setTimeout(() => {
        this.renderTaskPlanCard(plan);
        this.scrollToBottom();
      }, 600);
    });
  }

  /**
   * Add a scripted Alex message (non-typewriter, instant).
   * Used during plan approval flow for dialogue like "Let me brief the team".
   */
  addAlexDialogue(text: string): void {
    const workerId = this.getWorkerId();
    const msg: ChatMessage = {
      role: 'assistant',
      content: text,
      workerId,
      timestamp: Date.now(),
    };
    this.addToHistory(workerId, msg);
    this.renderMessages();
    this.scrollToBottom();
  }

  /**
   * Add a deliverable card in the chat flow.
   * Shows title, preview text, and a "View Full Report" button.
   */
  addDeliverableCard(deliverable: {
    id: string;
    title: string;
    content_preview?: string | null;
    content?: { content?: string } | null;
  }): void {
    const preview = deliverable.content_preview
      || deliverable.content?.content?.slice(0, 500)
      || 'No preview available';

    const card = document.createElement('div');
    card.className = 'chat-deliverable-card';
    card.innerHTML = `
      <div class="deliverable-card-title">${escapeHtml(deliverable.title)}</div>
      <div class="deliverable-card-preview">${escapeHtml(preview.slice(0, 300))}${preview.length > 300 ? '...' : ''}</div>
      <button class="deliverable-card-view-btn">📄 View Full Report</button>
    `;

    card.querySelector('.deliverable-card-view-btn')?.addEventListener('click', () => {
      window.dispatchEvent(new CustomEvent('view-deliverable', {
        detail: deliverable,
      }));
    });

    this.messagesEl.appendChild(card);
    this.scrollToBottom();
  }

  /**
   * Add an onboarding message with typewriter effect.
   * Directly renders in chat — does NOT go through the API.
   */
  addOnboardingMessage(text: string, senderName: string, onComplete?: () => void): void {
    // Cancel any running typewriter
    if (this.activeTypewriterInterval) {
      clearInterval(this.activeTypewriterInterval);
      this.activeTypewriterInterval = null;
    }

    const el = document.createElement('div');
    el.className = 'chat-msg assistant onboarding-msg';

    const nameSpan = document.createElement('span');
    nameSpan.className = 'chat-msg-name';
    nameSpan.textContent = senderName;
    el.appendChild(nameSpan);

    const textSpan = document.createElement('span');
    textSpan.className = 'chat-msg-text';
    textSpan.textContent = '';
    el.appendChild(textSpan);

    this.messagesEl.appendChild(el);
    this.scrollToBottom();

    // Typewriter animation
    let charIndex = 0;
    this.activeTypewriterInterval = setInterval(() => {
      if (charIndex < text.length) {
        textSpan.textContent += text[charIndex];
        charIndex++;
        this.scrollToBottom();
      } else {
        clearInterval(this.activeTypewriterInterval!);
        this.activeTypewriterInterval = null;
        onComplete?.();
      }
    }, TYPEWRITER_CHAR_DELAY);
  }

  /**
   * Add a styled inline button in the chat flow (e.g. [Show Me]).
   */
  addInlineButton(label: string, onClick: () => void): void {
    const wrapper = document.createElement('div');
    wrapper.className = 'chat-inline-btn-wrapper';

    const btn = document.createElement('button');
    btn.className = 'chat-inline-btn';
    btn.textContent = label;
    btn.addEventListener('click', () => {
      btn.disabled = true;
      btn.classList.add('clicked');
      onClick();
    });

    wrapper.appendChild(btn);
    this.messagesEl.appendChild(wrapper);
    this.scrollToBottom();
  }

  /**
   * Display research results as a formatted card in the chat.
   */
  addResearchResultsCard(headlines: ResearchHeadline[]): void {
    const el = document.createElement('div');
    el.className = 'chat-msg assistant';

    let html = '<div class="research-results-card">';
    html += '<div class="research-results-header">Research Results</div>';
    for (const h of headlines.slice(0, 5)) {
      html += `<div class="research-result-item">
        <span class="research-result-title">${escapeHtml(h.title)}</span>
        <span class="research-result-source">${escapeHtml(h.source)}</span>
      </div>`;
    }
    html += '</div>';
    el.innerHTML = html;

    this.messagesEl.appendChild(el);
    this.scrollToBottom();
  }

  /**
   * Display a unified stock analysis card (chart + info + expand button).
   * Optionally accepts an onExpand callback to open the deliverable modal.
   */
  addStockChartCard(data: StockAnalysisData, onExpand?: () => void): void {
    const el = document.createElement('div');
    el.className = 'chat-msg assistant';
    el.style.maxWidth = '100%';
    el.style.width = '100%';

    const isPositive = data.change.startsWith('+');
    const changeColor = isPositive ? '#22c55e' : '#ef4444';
    const arrow = isPositive ? '↑' : '↓';

    // Draw chart on offscreen canvas, then convert to <img> for proper sizing
    let chartDataUrl = '';
    if (data.pricePoints.length > 1) {
      const offscreen = document.createElement('canvas');
      offscreen.width = 640;
      offscreen.height = 160;
      this.drawStockChart(offscreen, data.pricePoints, isPositive);
      chartDataUrl = offscreen.toDataURL('image/png');
    }

    el.innerHTML = `
      <div class="stock-card${onExpand ? ' expandable' : ''}">
        <div class="stock-card-header">
          <div class="stock-card-ticker">
            <span class="stock-card-symbol">${escapeHtml(data.ticker)}</span>
            <span class="stock-card-name">${escapeHtml(data.companyName)}</span>
          </div>
          <div class="stock-card-price">
            <span class="stock-card-current">${escapeHtml(data.currentPrice)}</span>
            <span class="stock-card-change" style="color: ${changeColor}">${arrow} ${escapeHtml(data.change)} (${escapeHtml(data.changePercent)})</span>
          </div>
        </div>
        ${chartDataUrl ? `<img class="stock-chart-img" src="${chartDataUrl}" alt="Stock chart" />` : ''}
        <div class="stock-card-footer">
          <span class="stock-card-cap">Mkt Cap: ${escapeHtml(data.marketCap)}</span>
          <span class="stock-card-summary">${escapeHtml(data.summary)}</span>
        </div>
        ${onExpand ? '<button class="stock-card-expand-btn">📊 Expand Report</button>' : ''}
      </div>
    `;

    this.messagesEl.appendChild(el);

    // Wire expand button
    if (onExpand) {
      const expandBtn = el.querySelector('.stock-card-expand-btn');
      expandBtn?.addEventListener('click', onExpand);
    }

    this.scrollToBottom();
  }

  private drawStockChart(
    canvas: HTMLCanvasElement,
    points: Array<{ label: string; price: number }>,
    isPositive: boolean,
  ): void {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const W = canvas.width;
    const H = canvas.height;
    const pad = { top: 16, right: 16, bottom: 28, left: 50 };
    const chartW = W - pad.left - pad.right;
    const chartH = H - pad.top - pad.bottom;

    const prices = points.map(p => p.price);
    const minP = Math.min(...prices) * 0.998;
    const maxP = Math.max(...prices) * 1.002;
    const range = maxP - minP || 1;

    // Background — matches .stock-card bg so chart blends seamlessly
    ctx.fillStyle = 'rgba(15, 23, 41, 1)';
    ctx.fillRect(0, 0, W, H);

    // Grid lines
    ctx.strokeStyle = 'rgba(255,255,255,0.06)';
    ctx.lineWidth = 1;
    for (let i = 0; i <= 4; i++) {
      const y = pad.top + (chartH / 4) * i;
      ctx.beginPath();
      ctx.moveTo(pad.left, y);
      ctx.lineTo(W - pad.right, y);
      ctx.stroke();

      // Y-axis labels
      const price = maxP - (range / 4) * i;
      ctx.fillStyle = 'rgba(255,255,255,0.4)';
      ctx.font = '10px system-ui';
      ctx.textAlign = 'right';
      ctx.fillText(`$${price.toFixed(1)}`, pad.left - 6, y + 4);
    }

    // Compute chart points
    const chartPoints: [number, number][] = points.map((p, i) => [
      pad.left + (i / (points.length - 1)) * chartW,
      pad.top + (1 - (p.price - minP) / range) * chartH,
    ]);

    // Gradient fill under the line
    const gradient = ctx.createLinearGradient(0, pad.top, 0, pad.top + chartH);
    const lineColor = isPositive ? '#22c55e' : '#ef4444';
    gradient.addColorStop(0, isPositive ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)');
    gradient.addColorStop(1, 'rgba(0,0,0,0)');

    ctx.beginPath();
    ctx.moveTo(chartPoints[0][0], chartPoints[0][1]);
    for (let i = 1; i < chartPoints.length; i++) {
      // Smooth curve using quadratic bezier
      const prevX = chartPoints[i - 1][0];
      const prevY = chartPoints[i - 1][1];
      const currX = chartPoints[i][0];
      const currY = chartPoints[i][1];
      const cpx = (prevX + currX) / 2;
      ctx.quadraticCurveTo(prevX, prevY, cpx, (prevY + currY) / 2);
    }
    const lastPt = chartPoints[chartPoints.length - 1];
    ctx.lineTo(lastPt[0], lastPt[1]);
    ctx.lineTo(lastPt[0], pad.top + chartH);
    ctx.lineTo(chartPoints[0][0], pad.top + chartH);
    ctx.closePath();
    ctx.fillStyle = gradient;
    ctx.fill();

    // Line
    ctx.beginPath();
    ctx.moveTo(chartPoints[0][0], chartPoints[0][1]);
    for (let i = 1; i < chartPoints.length; i++) {
      const prevX = chartPoints[i - 1][0];
      const prevY = chartPoints[i - 1][1];
      const currX = chartPoints[i][0];
      const currY = chartPoints[i][1];
      const cpx = (prevX + currX) / 2;
      ctx.quadraticCurveTo(prevX, prevY, cpx, (prevY + currY) / 2);
    }
    ctx.lineTo(lastPt[0], lastPt[1]);
    ctx.strokeStyle = lineColor;
    ctx.lineWidth = 2.5;
    ctx.stroke();

    // Data points (dots)
    for (const [x, y] of chartPoints) {
      ctx.beginPath();
      ctx.arc(x, y, 3.5, 0, Math.PI * 2);
      ctx.fillStyle = lineColor;
      ctx.fill();
      ctx.beginPath();
      ctx.arc(x, y, 1.5, 0, Math.PI * 2);
      ctx.fillStyle = '#ffffff';
      ctx.fill();
    }

    // X-axis labels
    ctx.fillStyle = 'rgba(255,255,255,0.5)';
    ctx.font = '10px system-ui';
    ctx.textAlign = 'center';
    for (let i = 0; i < points.length; i++) {
      ctx.fillText(points[i].label, chartPoints[i][0], H - 6);
    }
  }

  /**
   * Add an inline phone number input directly in the chat flow.
   * Replaces the full-screen modal — feels more natural and less scammy.
   */
  addPhoneInput(onSubmit: (phoneNumber: string) => void, onSkip: () => void): void {
    const wrapper = document.createElement('div');
    wrapper.className = 'chat-phone-input-wrapper';

    wrapper.innerHTML = `
      <div class="chat-phone-row">
        <input type="tel" class="chat-phone-input" placeholder="(555) 555-5555" maxlength="14" />
        <button class="chat-phone-send">Call Me</button>
      </div>
      <button class="chat-phone-skip">Skip</button>
    `;

    this.messagesEl.appendChild(wrapper);
    this.scrollToBottom();

    const input = wrapper.querySelector('.chat-phone-input') as HTMLInputElement;
    const sendBtn = wrapper.querySelector('.chat-phone-send')!;
    const skipBtn = wrapper.querySelector('.chat-phone-skip')!;

    // US phone number formatting as user types
    input.addEventListener('input', () => {
      const digits = input.value.replace(/\D/g, '').slice(0, 10);
      if (digits.length === 0) {
        input.value = '';
      } else if (digits.length <= 3) {
        input.value = `(${digits}`;
      } else if (digits.length <= 6) {
        input.value = `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
      } else {
        input.value = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
      }
    });

    const doSubmit = () => {
      const digits = input.value.replace(/\D/g, '');
      if (digits.length >= 10) {
        const e164 = `+1${digits}`;
        input.disabled = true;
        (sendBtn as HTMLButtonElement).disabled = true;
        (skipBtn as HTMLButtonElement).disabled = true;
        wrapper.style.opacity = '0.5';
        wrapper.style.pointerEvents = 'none';
        onSubmit(e164);
      } else {
        input.classList.add('shake');
        setTimeout(() => input.classList.remove('shake'), 500);
      }
    };

    sendBtn.addEventListener('click', doSubmit);
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') doSubmit();
    });

    skipBtn.addEventListener('click', () => {
      wrapper.style.opacity = '0.5';
      wrapper.style.pointerEvents = 'none';
      onSkip();
    });

    // Focus the input
    requestAnimationFrame(() => input.focus());
  }

  /**
   * Add a task card that shows a task in progress.
   * Returns a handle with `complete()` to update the card to done state.
   */
  addTaskCard(description: string, workerName: string): { complete: () => void } {
    const el = document.createElement('div');
    el.className = 'chat-task-card';

    el.innerHTML = `
      <div class="chat-task-header">
        <span class="chat-task-icon">🔍</span>
        <span class="chat-task-desc">${escapeHtml(description)}</span>
      </div>
      <div class="chat-task-meta">
        <span class="chat-task-worker">Assigned to ${escapeHtml(workerName)}</span>
        <span class="chat-task-status">
          <span class="chat-task-spinner"></span>
          In progress
        </span>
      </div>
      <div class="chat-task-progress"><div class="chat-task-progress-bar"></div></div>
    `;

    this.messagesEl.appendChild(el);
    this.scrollToBottom();

    return {
      complete: () => {
        el.classList.add('complete');
        const iconEl = el.querySelector('.chat-task-icon');
        if (iconEl) iconEl.textContent = '✅';
        const statusEl = el.querySelector('.chat-task-status');
        if (statusEl) statusEl.innerHTML = 'Complete';
        const progressBar = el.querySelector('.chat-task-progress-bar') as HTMLElement;
        if (progressBar) {
          progressBar.style.animation = 'none';
          progressBar.style.width = '100%';
        }
      },
    };
  }

  /**
   * Render a task plan card from Alex with an "Approve Plan" button.
   * When approved, calls the backend to execute the plan.
   */
  private renderTaskPlanCard(plan: TaskPlan): void {
    const el = document.createElement('div');
    el.className = 'chat-task-plan-card';

    // Task type icons
    const typeIcons: Record<string, string> = {
      research: '🔍', draft: '✏️', analyze: '📊', plan: '📋', general: '📄',
    };

    let tasksHtml = '';
    for (const task of plan.tasks) {
      const icon = typeIcons[task.taskType] || '📄';
      tasksHtml += `
        <div class="plan-task-item">
          <span class="plan-task-icon">${icon}</span>
          <div class="plan-task-info">
            <span class="plan-task-desc">${escapeHtml(task.description)}</span>
            <span class="plan-task-worker">${escapeHtml(task.workerRole)}</span>
          </div>
        </div>`;
    }

    el.innerHTML = `
      <div class="plan-card-header">
        <span class="plan-card-icon">📋</span>
        <span class="plan-card-title">Task Plan</span>
      </div>
      <div class="plan-card-summary">${escapeHtml(plan.summary)}</div>
      <div class="plan-task-list">${tasksHtml}</div>
      <button class="plan-approve-btn">Approve Plan</button>
    `;

    this.messagesEl.appendChild(el);
    this.scrollToBottom();

    // Wire approve button
    const approveBtn = el.querySelector('.plan-approve-btn') as HTMLButtonElement;
    approveBtn.addEventListener('click', async () => {
      approveBtn.disabled = true;
      approveBtn.textContent = 'Starting tasks...';
      approveBtn.classList.add('approved');

      try {
        const result = await dataManager.executePlan(plan);
        approveBtn.textContent = `✅ ${result.tasks.length} tasks assigned!`;

        // Dispatch event so task progress card and 3D scene can react
        window.dispatchEvent(new CustomEvent('plan-approved', {
          detail: { planId: result.planId, summary: plan.summary, tasks: result.tasks },
        }));
      } catch (err) {
        approveBtn.textContent = 'Failed — try again';
        approveBtn.disabled = false;
        approveBtn.classList.remove('approved');
        console.error('Plan execution failed:', err);
      }
    });
  }

  /**
   * Pulse the chat input to invite the user's first message.
   */
  pulseInput(): void {
    this.inputEl.classList.add('pulsing');
    this.inputEl.addEventListener('focus', () => {
      this.inputEl.classList.remove('pulsing');
    }, { once: true });
  }
}
